import * as z from 'zod'
import { getURLSearchParams } from '~/utility/url-params'
import { langFallback$Fetch } from '../../../lang/utils/langfetch'
import { moneyZ } from '../schema'
import {
  Quote,
  QuoteRequestParams,
  quoteRequestParamsZ,
  quoteZ,
} from './schema'

// transform from app params to ajax params
const paramsTransformer = quoteRequestParamsZ.transform((params) => {
  return {
    arrive: params.stayRange.start,
    depart: params.stayRange.end,
    adults: params.party.adults,
    childAges: params.party.childAges.join(','),
    pitchtype: params.pitchtypeId.toString(),
    chargetype: params.chargetypeId
      ? params.chargetypeId.toString()
      : undefined,
    selectedExtras: JSON.stringify(params.selectedExtras),
  }
})

// extra in the ajax results
const extraZ = z.object({
  pricingDisplay: z.string(),
  price: moneyZ,
  pitchtypeExtra: z
    .object({
      included: z.number().nullable(),
      maximum: z.number().nullable(),
    })
    .optional()
    .nullable(),
  extra: z.object({
    description: z.string(),
    descriptionNeedsTranslation: z.boolean().optional(),
    id: z.string(),
    max: z
      .number()
      .nullable()
      .transform((m) => (m === null ? Number.POSITIVE_INFINITY : m)),
    name: z.string(),
    terms: z.string().array().nullable().optional(),
  }),
})

// transform ajax results to app results
const ajaxResponseZ = z.object({
  balanceDueDate: z.string().nullable(),
  refundUntilDate: z.string().optional(),
  commission: z.number(),
  paymentWhen: quoteZ.shape.paymentWhen,
  paypalEnabled: z.boolean(),
  result: z.object({
    arrivalTaxes: z
      .object({
        amount: moneyZ,
        hidden: z.boolean(),
        type: z.string(),
      })
      .array()
      .transform((arrivalTaxes) =>
        arrivalTaxes.map((arrivalTax) => {
          return {
            isHidden: arrivalTax.hidden,
            ...arrivalTax,
          }
        }),
      ),
    chargeTypeId: z.number().transform((val) => val.toString()),
    compulsoryExtras: extraZ.array().transform((extras) =>
      extras.map((extra) => {
        return {
          ...extra,
          ...extra.extra,
          isCompulsory: true,
          descriptionNeedsTranslation:
            !!extra.extra.descriptionNeedsTranslation,
        }
      }),
    ),
    deposit: moneyZ,
    extras: extraZ.array().transform((extras) =>
      extras.map((extra) => {
        return {
          ...extra,
          ...extra.extra,
          isCompulsory: extra.compulsory || false,
          descriptionNeedsTranslation:
            !!extra.extra.descriptionNeedsTranslation,
        }
      }),
    ),
    // key is the extra id, and value is the quantity
    extrasInitial: z.record(z.number()),
    price: moneyZ,
    priceAccommodation: moneyZ,
    remainder: moneyZ,
    party: z.object({
      adults: z.number(),
      childAges: z.number().array(),
      children: z.number(),
      convertedChildrenToAdult: z.number(),
      infants: z.number(),
      maxChildAge: z.number(),
      maxInfantAge: z.number(),
      originalAdults: z.number(),
      size: z.number(),
      converted: z.object({
        adults: z.number(),
        children: z.number(),
        childrenAges: z.number().array(),
        infants: z.number(),
        infantsAges: z.number().array(),
      }),
    }),
    quote: z.object({
      pitchesToSell: z.number(),
    }),
    taxes: z
      .object({
        amount: moneyZ,
        commissionable: z.boolean(),
        hidden: z.boolean(),
        inclusive: z.boolean(),
        name: z.string(),
        rate: z.number(),
        taxType: z.string(),
        type: z.string(),
      })
      .array()
      .transform((taxes) =>
        taxes.map((tax) => {
          return {
            ...tax,
            isCommissionable: tax.commissionable,
            isHidden: tax.hidden,
            isInclusive: tax.inclusive,
          }
        }),
      ),
  }),
})

const responseTransformer = ajaxResponseZ.transform((response) => {
  return {
    ...response,
    ...response.result,
    ...response.result.quote,
    chargetypeId: response.result.chargeTypeId,
    convertedChildren: response.result.party.convertedChildrenToAdult,
    priceTotal: response.result.price,
    pricedTax: response.result.taxes,
    extras: response.result.extras,
    extrasInitial: response.result.extrasInitial,
    compulsoryExtras: response.result.compulsoryExtras,
    convertedParty: response.result.party.converted,
  }
})

export class QuoteRepositoryFetch {
  async getQuote(
    request: QuoteRequestParams,
    langCode: string,
  ): Promise<Quote | null> {
    const result: any = await langFallback$Fetch(
      langCode,
      `/_/endpoints/booking/request/?${getURLSearchParams(
        paramsTransformer.parse(request),
      ).toString()}`,
    )
    const parsedResult =
      result === null || result?.result?.price === null
        ? null
        : responseTransformer.parse(result)
    if (parsedResult === null) {
      throw new Error('Failed to get quote')
    }
    // fix compulsory extras incase backend does not have compulsory yet
    const compulsoryIds = result.result.compulsoryExtras.map(
      (extra) => extra.extra.id,
    )
    parsedResult.extras.forEach((extra) => {
      if (compulsoryIds.includes(extra.id)) {
        extra.isCompulsory = true
      }
    })
    return parsedResult
  }
}
